/**
 * Single source of truth for subscription plan features.
 * Used by: PricingPage, ChildSubscriptionModal, upgrade modal
 */

// Feature keys that map to translation keys
export const FEATURE_KEYS = {
  BASIC_ACTIVITIES: 'basic_activities',
  ADVANCED_ACTIVITIES: 'advanced_activities',
  UNLIMITED_LEARNING: 'unlimited_learning',
  DAILY_GOALS: 'daily_goals',
  DETAILED_PROGRESS: 'detailed_progress',
  PRIORITY_SUPPORT: 'priority_support',
};

// Plan feature matrix - defines which features are enabled for each plan
export const PLAN_FEATURES = {
  free: {
    [FEATURE_KEYS.BASIC_ACTIVITIES]: true,
    [FEATURE_KEYS.ADVANCED_ACTIVITIES]: false,
    [FEATURE_KEYS.UNLIMITED_LEARNING]: false,
    [FEATURE_KEYS.DAILY_GOALS]: true,
    [FEATURE_KEYS.DETAILED_PROGRESS]: false,
    [FEATURE_KEYS.PRIORITY_SUPPORT]: false,
  },
  pro: {
    [FEATURE_KEYS.BASIC_ACTIVITIES]: true,
    [FEATURE_KEYS.ADVANCED_ACTIVITIES]: true,
    [FEATURE_KEYS.UNLIMITED_LEARNING]: true,
    [FEATURE_KEYS.DAILY_GOALS]: true,
    [FEATURE_KEYS.DETAILED_PROGRESS]: false,
    [FEATURE_KEYS.PRIORITY_SUPPORT]: false,
  },
  premium: {
    [FEATURE_KEYS.BASIC_ACTIVITIES]: true,
    [FEATURE_KEYS.ADVANCED_ACTIVITIES]: true,
    [FEATURE_KEYS.UNLIMITED_LEARNING]: true,
    [FEATURE_KEYS.DAILY_GOALS]: true,
    [FEATURE_KEYS.DETAILED_PROGRESS]: true,
    [FEATURE_KEYS.PRIORITY_SUPPORT]: true,
  },
};

// Ordered list of all features (for consistent display order)
export const FEATURE_ORDER = [
  FEATURE_KEYS.BASIC_ACTIVITIES,
  FEATURE_KEYS.ADVANCED_ACTIVITIES,
  FEATURE_KEYS.UNLIMITED_LEARNING,
  FEATURE_KEYS.DAILY_GOALS,
  FEATURE_KEYS.DETAILED_PROGRESS,
  FEATURE_KEYS.PRIORITY_SUPPORT,
];

// Features that are exclusive to Premium (used for upgrade modal)
export const PREMIUM_EXCLUSIVE_FEATURES = FEATURE_ORDER.filter(
  (key) => PLAN_FEATURES.premium[key] && !PLAN_FEATURES.pro[key]
);

/**
 * Get features for a specific plan with translated labels
 * @param {string} plan - 'free', 'pro', or 'premium'
 * @param {function} t - translation function
 * @param {string} translationPrefix - prefix for translation keys (e.g., 'pricing' or 'dashboard.index.children.subscribe_modal')
 * @param {object} activityCounts - optional object with { freeActivitiesCount, paidActivitiesCount }
 * @returns {Array} Array of { key, label, enabled } objects
 */
export const getPlanFeatures = (plan, t, translationPrefix = 'plan_features', activityCounts = null) => {
  const planConfig = PLAN_FEATURES[plan];
  if (!planConfig) {
    console.warn(`Unknown plan: ${plan}`);
    return [];
  }

  return FEATURE_ORDER.map((key) => {
    let label;
    if (key === FEATURE_KEYS.BASIC_ACTIVITIES && activityCounts) {
      label = t(`${translationPrefix}.${key}`, { count: activityCounts.freeActivitiesCount });
    } else if (key === FEATURE_KEYS.ADVANCED_ACTIVITIES && activityCounts) {
      label = t(`${translationPrefix}.${key}`, { count: activityCounts.paidActivitiesCount });
    } else {
      label = t(`${translationPrefix}.${key}`);
    }
    return {
      key,
      label,
      enabled: planConfig[key],
    };
  });
};

/**
 * Get features comparison for multiple plans (useful for pricing cards)
 * @param {function} t - translation function
 * @param {string} translationPrefix - prefix for translation keys
 * @param {object} activityCounts - optional object with { freeActivitiesCount, paidActivitiesCount }
 * @returns {Array} Array of { key, label, free, pro, premium } objects
 */
export const getFeatureComparison = (t, translationPrefix = 'plan_features', activityCounts = null) => {
  return FEATURE_ORDER.map((key) => {
    let label;
    if (key === FEATURE_KEYS.BASIC_ACTIVITIES && activityCounts) {
      label = t(`${translationPrefix}.${key}`, { count: activityCounts.freeActivitiesCount });
    } else if (key === FEATURE_KEYS.ADVANCED_ACTIVITIES && activityCounts) {
      label = t(`${translationPrefix}.${key}`, { count: activityCounts.paidActivitiesCount });
    } else {
      label = t(`${translationPrefix}.${key}`);
    }
    return {
      key,
      label,
      free: PLAN_FEATURES.free[key],
      pro: PLAN_FEATURES.pro[key],
      premium: PLAN_FEATURES.premium[key],
    };
  });
};

/**
 * Get features that are exclusive to a higher plan (for upgrade modals)
 * @param {string} fromPlan - current plan (e.g., 'pro')
 * @param {string} toPlan - target plan (e.g., 'premium')
 * @param {function} t - translation function
 * @param {string} translationPrefix - prefix for translation keys
 * @param {object} activityCounts - optional object with { freeActivitiesCount, paidActivitiesCount }
 * @returns {Array} Array of { key, label } objects for features gained
 */
export const getUpgradeFeatures = (fromPlan, toPlan, t, translationPrefix = 'plan_features', activityCounts = null) => {
  const fromConfig = PLAN_FEATURES[fromPlan];
  const toConfig = PLAN_FEATURES[toPlan];

  if (!fromConfig || !toConfig) {
    return [];
  }

  return FEATURE_ORDER
    .filter((key) => toConfig[key] && !fromConfig[key])
    .map((key) => {
      let label;
      if (key === FEATURE_KEYS.BASIC_ACTIVITIES && activityCounts) {
        label = t(`${translationPrefix}.${key}`, { count: activityCounts.freeActivitiesCount });
      } else if (key === FEATURE_KEYS.ADVANCED_ACTIVITIES && activityCounts) {
        label = t(`${translationPrefix}.${key}`, { count: activityCounts.paidActivitiesCount });
      } else {
        label = t(`${translationPrefix}.${key}`);
      }
      return {
        key,
        label,
      };
    });
};
